import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../../jsx/layouts/PageTitle";
import { getUddyamaanApplicants } from "../../../services/adminApiService/uddyamaanApi/uddyamaanApi";

const statusStyles = {
  pending:            { background: "#FFF7E0", color: "#FAAD14", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block" },
  approved:           { background: "#E7F9EF", color: "#52C41A", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block", border: "1px solid #B7E4C7" },
  rejected:           { background: "#FFE0E0", color: "#FF4D4F", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block" },
  review:             { background: "#F3F0FF", color: "#B18AFF", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block" },
  revision_requested: { background: "#FFF3CD", color: "#856404", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block" },
  forwarded:          { background: "#E0F7FA", color: "#00838F", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block", border: "1px solid #80DEEA" },
  selected:           { background: "#FEF3C7", color: "#B7791F", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block", border: "1px solid #F6C453" },
};

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  review: "Under Review",
  revision_requested: "Revision Requested",
  forwarded: "Forwarded",
  selected: "Selected",
};

const STAGE_LABELS = { nyaya_panchayat: "Nyaya Panchayat Level", block: "Block Level", district: "District Level" };
const getStageLabel = (app) => {
  if (app.current_stage === "nyaya_panchayat" && app.jurisdiction_type === "ward") return "Ward Level";
  return STAGE_LABELS[app.current_stage] || "In Progress";
};
const stageBadgeStyle = { background: "#EEF2FF", color: "#4338CA", borderRadius: 12, padding: "4px 14px", fontWeight: 500, fontSize: 14, display: "inline-block", border: "1px solid #C7D2FE" };

const SCHEME_LABEL = { uddyamaan: 'Udiyamaan Unnayan Yojna', protsaahan: 'Protsaahan' };

const UddyamaanApplicants = ({ schemeType = 'uddyamaan' }) => {
  const navigate = useNavigate();
  const basePath = schemeType === 'protsaahan' ? '/protsaahan-yojana' : '/uddyamaan-unnayan-yojana';
  const schemeLabel = SCHEME_LABEL[schemeType] || SCHEME_LABEL.uddyamaan;
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, search, statusFilter, schemeType]);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await getUddyamaanApplicants(currentPage, search, statusFilter, 10, schemeType);
      setApplications(res?.data?.data?.data || []);
      setTotal(res?.data?.data?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handlePageChange = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await getUddyamaanApplicants(1, search, statusFilter, 5000, schemeType);
      const rows = res?.data?.data?.data || [];
      const headers = ["#", "Name", "Email", "Program", "District", "DOB", "Status"];
      const csvRows = [
        headers.join(","),
        ...rows.map((app, idx) => [
          idx + 1,
          `"${(app.name || "").replace(/"/g, '""')}"`,
          `"${(app.user?.email || "").replace(/"/g, '""')}"`,
          `"${(app.program?.title || "").replace(/"/g, '""')}"`,
          `"${(app.district || "").replace(/"/g, '""')}"`,
          app.dob ? new Date(app.dob).toLocaleDateString("en-IN") : "",
          STATUS_LABEL[app.status] || app.status,
        ].join(",")),
      ];
      const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filterSuffix = statusFilter ? `_${statusFilter}` : "";
      a.download = `${schemeType}_applicants${filterSuffix}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Applicants" motherMenu={schemeLabel} />
      <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <h2>{schemeLabel} — Applicants</h2>

        <div className="row g-2 mb-4 mt-2">
          <div className="col-12 col-md">
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search by name or district..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="form-control"
                style={{ padding: "10px 40px 10px 16px", borderRadius: 8, fontSize: 16 }}
              />
              <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#888" }}>
                <i className="fa fa-search" />
              </span>
            </div>
          </div>
          <div className="col-12 col-md-auto">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="form-select"
              style={{ padding: "10px 40px 10px 12px" }}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="review">Under Review</option>
              <option value="revision_requested">Revision Requested</option>
              <option value="forwarded">Forwarded</option>
              <option value="selected">Selected</option>
            </select>
          </div>
          <div className="col-12 col-md-auto">
            <button
              className="btn"
              onClick={handleExportCSV}
              disabled={exporting}
              style={{ background: "#7C3AED", color: "#fff", borderRadius: 8, padding: "10px 20px", fontWeight: 500, whiteSpace: "nowrap" }}
            >
              {exporting ? "Exporting..." : "⬇ Export CSV"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <div className="w-100 table-responsive">
              <table className="display w-100 dataTable table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th className="d-none d-md-table-cell">Program</th>
                    <th className="d-none d-md-table-cell">District</th>
                    <th className="d-none d-md-table-cell">DOB</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length > 0 ? (
                    applications.map((app, idx) => (
                      <tr key={app.id}>
                        <td>{(currentPage - 1) * perPage + idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{app.name}</div>
                          <div style={{ color: "#9CA3AF", fontSize: 12 }}>{app.user?.email || ""}</div>
                          <div className="d-md-none text-muted" style={{ fontSize: 11 }}>
                            {[app.program?.title, app.district, app.dob ? new Date(app.dob).toLocaleDateString("en-IN") : null]
                              .filter(Boolean).join(" · ")}
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{app.program?.title || "—"}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{app.district}</td>
                        <td className="d-none d-md-table-cell" style={{ fontSize: 14 }}>{app.dob ? new Date(app.dob).toLocaleDateString("en-IN") : "—"}</td>
                        <td>
                          {app.decision_made_at ? (
                            <span style={statusStyles[app.status] || statusStyles.pending}>
                              {STATUS_LABEL[app.status] || app.status}
                            </span>
                          ) : app.is_forwarded ? (
                            <span style={statusStyles.forwarded}>Forwarded to {getStageLabel(app)}</span>
                          ) : (
                            <span style={stageBadgeStyle}>
                              {getStageLabel(app)}
                            </span>
                          )}
                        </td>
                        <td>
                          <i
                            className="fa fa-eye"
                            style={{ cursor: "pointer", color: "#888", fontSize: 18 }}
                            onClick={() => navigate(`${basePath}/assessment/${app.id}`)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">No applicants found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ReactPaginate
        nextLabel="next >"
        onPageChange={handlePageChange}
        pageRangeDisplayed={8}
        marginPagesDisplayed={2}
        pageCount={Math.ceil(total / perPage)}
        previousLabel="< previous"
        pageClassName="page-item mx-1 my-1"
        pageLinkClassName="page-link"
        previousClassName="page-item mx-1 my-1"
        previousLinkClassName="page-link"
        nextClassName="page-item mx-1 my-1"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item mx-1 my-1"
        breakLinkClassName="page-link"
        containerClassName="pagination d-flex flex-wrap"
        activeClassName="active"
        renderOnZeroPageCount={null}
        forcePage={currentPage - 1}
      />
    </>
  );
};

export default UddyamaanApplicants;
